import { useReducer, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import TableChartIcon from "@mui/icons-material/TableChart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CropFreeIcon from "@mui/icons-material/CropFree";
import { v4 as uuidv4 } from "uuid";
import { StepConfigurar } from "./StepConfigurar";
import { StepCargar } from "./StepCargar";
import { StepOcr } from "./StepOcr";
import { StepMetadatos } from "./StepMetadatos";
import { StepConfirmar } from "./StepConfirmar";
import { StepRegiones } from "./StepRegiones";
import { getLoteMaximo } from "../../../helpers/ContenidosData";

// ─── Reducer ─────────────────────────────────────────────────────────────────

const initialState = {
  activeStep: 0,
  // Step 0 — Configurar
  id_tipo_contenidos: null,
  id_ocr_plantilla: null,
  metadatosDefinition: [],
  useOcr: true,
  // Step 1 — Cargar
  loteBase: null,
  contenidos: [],
  uploadMode: "fixed",
  imagesPerGroup: 1,
  stagingImages: [],
  // Step 2 — Regiones
  regiones: [],
  // Step 3 — OCR
  ocrLoading: false,
  // Step 4 — Metadatos
  fieldModes: {},
  manualValues: {},
  contenidoMetadata: {},
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_ACTIVE_STEP":
      return { ...state, activeStep: action.payload };

    case "SET_TIPO_CONTENIDO": {
      const { id_tipo_contenidos, id_ocr_plantilla, metadatosDefinition } = action.payload;
      return {
        ...state,
        id_tipo_contenidos,
        id_ocr_plantilla,
        metadatosDefinition,
        contenidos: state.contenidos.map((c) => ({ ...c, status: "pending", ocrResult: null, errorMsg: "" })),
        regiones: [],
        fieldModes: {},
        manualValues: {},
        contenidoMetadata: {},
        stagingImages: [],
      };
    }

    case "SET_REGIONES":
      return { ...state, regiones: action.payload };

    case "SET_PLANTILLA":
      return { ...state, id_ocr_plantilla: action.payload };

    case "SET_USE_OCR":
      return {
        ...state,
        useOcr: action.payload,
        ...(action.payload === false ? {
          id_ocr_plantilla: null,
          regiones: [],
          contenidos: state.contenidos.map((c) => ({ ...c, status: "pending", ocrResult: null, errorMsg: "" })),
          fieldModes: {},
          manualValues: {},
          contenidoMetadata: {},
        } : {}),
      };

    // ── Staging images (fixed mode) ──────────────────────────────────────────

    case "ADD_STAGING_IMAGES": {
      const MAX = 200;
      const remaining = MAX - state.stagingImages.length;
      const toAdd = action.payload.slice(0, remaining);
      return { ...state, stagingImages: [...state.stagingImages, ...toAdd] };
    }

    case "REMOVE_STAGING_IMAGE":
      return { ...state, stagingImages: state.stagingImages.filter((img) => img.imageId !== action.payload) };

    case "SET_LOTE_BASE":
      return { ...state, loteBase: action.payload };

    case "SET_UPLOAD_MODE":
      return { ...state, uploadMode: action.payload, stagingImages: [], contenidos: [] };

    case "SET_IMAGES_PER_GROUP":
      return { ...state, imagesPerGroup: Math.max(1, parseInt(action.payload, 10) || 1) };

    case "APPLY_GROUPING": {
      // action.payload: { grupos: [{ contenidoId, images }] }
      return { ...state, contenidos: action.payload.grupos, stagingImages: [] };
    }

    // ── Contenidos ────────────────────────────────────────────────────────────

    case "ADD_CONTENIDO": {
      const MAX_C = 100;
      if (state.contenidos.length >= MAX_C) return state;
      return {
        ...state,
        contenidos: [
          ...state.contenidos,
          {
            contenidoId: action.payload.contenidoId,
            images: action.payload.images,
            status: "pending",
            ocrResult: null,
            errorMsg: "",
          },
        ],
      };
    }

    case "REMOVE_CONTENIDO":
      return { ...state, contenidos: state.contenidos.filter((c) => c.contenidoId !== action.payload) };

    case "REMOVE_IMAGE_FROM_CONTENIDO": {
      const { contenidoId, imageId } = action.payload;
      return {
        ...state,
        contenidos: state.contenidos
          .map((c) =>
            c.contenidoId === contenidoId
              ? { ...c, images: c.images.filter((img) => img.imageId !== imageId) }
              : c
          )
          .filter((c) => c.images.length > 0),
      };
    }

    case "SET_CONTENIDO_OCR_RESULT": {
      const { contenidoId, status, ocrResult, errorMsg } = action.payload;
      return {
        ...state,
        contenidos: state.contenidos.map((c) =>
          c.contenidoId === contenidoId ? { ...c, status, ocrResult, errorMsg } : c
        ),
      };
    }

    case "RESET_FAILED_CONTENIDOS":
      return {
        ...state,
        contenidos: state.contenidos.map((c) =>
          c.status === "error" ? { ...c, status: "pending", ocrResult: null, errorMsg: "" } : c
        ),
      };

    case "RESET_ALL_CONTENIDOS":
      return {
        ...state,
        contenidos: state.contenidos.map((c) => ({ ...c, status: "pending", ocrResult: null, errorMsg: "" })),
      };

    case "RESET_CONTENIDO":
      return {
        ...state,
        contenidos: state.contenidos.map((c) =>
          c.contenidoId === action.payload ? { ...c, status: "pending", ocrResult: null, errorMsg: "" } : c
        ),
      };

    case "SET_OCR_LOADING":
      return { ...state, ocrLoading: action.payload };

    case "INIT_CONTENIDO_METADATA": {
      const hasOcrSource = state.useOcr && (state.id_ocr_plantilla || state.regiones?.length > 0);
      const workContenidos = hasOcrSource
        ? state.contenidos.filter((c) => c.status === "success")
        : state.contenidos;

      const contenidoMetadata = {};
      workContenidos.forEach((c) => {
        contenidoMetadata[c.contenidoId] = {};
        if (c.ocrResult?.campos) {
          c.ocrResult.campos.forEach((campo) => {
            if (campo.id_metadatos) {
              contenidoMetadata[c.contenidoId][campo.id_metadatos] = campo.valor_extraido || "";
            }
          });
        }
        (state.metadatosDefinition || []).forEach((meta) => {
          if (contenidoMetadata[c.contenidoId][meta.id] === undefined) {
            contenidoMetadata[c.contenidoId][meta.id] = "";
          }
        });
      });

      const fieldModes = {};
      (state.metadatosDefinition || []).forEach((meta) => {
        const hasOcr =
          hasOcrSource &&
          workContenidos.some((c) => {
            const val = contenidoMetadata[c.contenidoId]?.[meta.id];
            return val && val !== "";
          });
        fieldModes[meta.id] = hasOcr ? "ocr" : "manual";
      });

      return { ...state, contenidoMetadata, fieldModes };
    }

    case "SET_FIELD_MODE": {
      const { id_metadatos, mode } = action.payload;
      return { ...state, fieldModes: { ...state.fieldModes, [id_metadatos]: mode } };
    }

    case "SET_MANUAL_VALUE": {
      const { id_metadatos, value } = action.payload;
      const newManualValues = { ...state.manualValues, [id_metadatos]: value };
      const newContenidoMeta = { ...state.contenidoMetadata };
      Object.keys(newContenidoMeta).forEach((cid) => {
        newContenidoMeta[cid] = { ...newContenidoMeta[cid], [id_metadatos]: value };
      });
      return { ...state, manualValues: newManualValues, contenidoMetadata: newContenidoMeta };
    }

    case "SET_CONTENIDO_METADATA_VALUE": {
      const { contenidoId, id_metadatos, value } = action.payload;
      return {
        ...state,
        contenidoMetadata: {
          ...state.contenidoMetadata,
          [contenidoId]: { ...(state.contenidoMetadata[contenidoId] || {}), [id_metadatos]: value },
        },
      };
    }

    case "RESET":
      return { ...initialState };

    default:
      return state;
  }
}

// ─── Step definitions ─────────────────────────────────────────────────────────

const ALL_STEPS = [
  { label: "Configurar", key: "configurar", icon: <SettingsIcon /> },
  { label: "Cargar", key: "cargar", icon: <CloudUploadIcon /> },
  { label: "Regiones", key: "regiones", icon: <CropFreeIcon /> },
  { label: "OCR", key: "ocr", icon: <DocumentScannerIcon /> },
  { label: "Metadatos", key: "metadatos", icon: <TableChartIcon /> },
  { label: "Confirmar", key: "confirmar", icon: <CheckCircleIcon /> },
];

// ─── Styled components ───────────────────────────────────────────────────────

const StyledStepLabel = styled(StepLabel)(({ theme }) => ({
  "& .MuiStepLabel-label": {
    fontSize: "0.75rem",
    fontWeight: 500,
  },
  "& .MuiStepLabel-label.Mui-active": {
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
}));

// ─── Component ───────────────────────────────────────────────────────────────

export const AltaMasivaOcrDialog = ({ open, onClose, onSuccess, idCaja, id_cuentas_localizaciones, cuentas_parametros }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (open && idCaja) {
      getLoteMaximo(idCaja).then((lote) => dispatch({ type: "SET_LOTE_BASE", payload: lote }));
    }
  }, [open, idCaja]);

  const hasPlantilla = !!state.id_ocr_plantilla;

  const currentKey = ALL_STEPS[state.activeStep]?.key;

  // canAdvance rules per step
  const canAdvance = (() => {
    switch (currentKey) {
      case "configurar":
        return !!state.id_tipo_contenidos;
      case "cargar":
        if (state.uploadMode === "fixed") return state.stagingImages.length > 0;
        return state.contenidos.length > 0;
      case "regiones":
        return true;
      case "ocr": {
        const successCount = state.contenidos.filter((c) => c.status === "success").length;
        const totalProcessed = state.contenidos.filter((c) => c.status !== "pending").length;
        return successCount > 0 && totalProcessed === state.contenidos.length && !state.ocrLoading;
      }
      case "metadatos":
        return true;
      case "confirmar":
        return false; // no Next on final step
      default:
        return false;
    }
  })();

  const handleNext = () => {
    const nextKeyMap = {
      configurar: "cargar",
      cargar: !state.useOcr ? "metadatos" : (hasPlantilla ? "ocr" : "regiones"),
      regiones: state.regiones?.length > 0 ? "ocr" : "metadatos",
      ocr: "metadatos",
      metadatos: "confirmar",
    };

    // Apply grouping for fixed mode before leaving cargar
    if (currentKey === "cargar" && state.uploadMode === "fixed" && state.stagingImages.length > 0) {
      const n = state.imagesPerGroup;
      const grupos = [];
      for (let i = 0; i < state.stagingImages.length; i += n) {
        grupos.push({
          contenidoId: uuidv4(),
          images: state.stagingImages.slice(i, i + n),
        });
      }
      dispatch({ type: "APPLY_GROUPING", payload: { grupos } });
    }

    const nextKey = nextKeyMap[currentKey];
    if (!nextKey) return;

    // Init contenido metadata whenever transitioning to metadatos
    if (nextKey === "metadatos") {
      dispatch({ type: "INIT_CONTENIDO_METADATA" });
    }

    const nextIdx = ALL_STEPS.findIndex((s) => s.key === nextKey);
    if (nextIdx >= 0) dispatch({ type: "SET_ACTIVE_STEP", payload: nextIdx });
  };

  const handleBack = () => {
    const prevKeyMap = {
      cargar: "configurar",
      regiones: "cargar",
      ocr: hasPlantilla ? "cargar" : "regiones",
      metadatos: !state.useOcr ? "cargar" : (state.regiones?.length > 0 || hasPlantilla ? "ocr" : "regiones"),
      confirmar: "metadatos",
    };
    const prevKey = prevKeyMap[currentKey];
    if (!prevKey) return;
    const prevIdx = ALL_STEPS.findIndex((s) => s.key === prevKey);
    if (prevIdx >= 0) dispatch({ type: "SET_ACTIVE_STEP", payload: prevIdx });
  };

  const handleClose = () => {
    dispatch({ type: "RESET" });
    onClose();
  };

  const handleSuccess = () => {
    dispatch({ type: "RESET" });
    if (onSuccess) onSuccess();
    onClose();
  };

  const isFirstStep = state.activeStep === 0;
  const isLastStep = state.activeStep === ALL_STEPS.length - 1;

  const renderStep = () => {
    switch (currentKey) {
      case "configurar":
        return <StepConfigurar state={state} dispatch={dispatch} />;
      case "cargar":
        return <StepCargar state={state} dispatch={dispatch} />;
      case "regiones":
        return <StepRegiones state={state} dispatch={dispatch} />;
      case "ocr":
        return <StepOcr state={state} dispatch={dispatch} />;
      case "metadatos":
        return <StepMetadatos state={state} dispatch={dispatch} />;
      case "confirmar":
        return (
          <StepConfirmar
            state={state}
            dispatch={dispatch}
            idCaja={idCaja}
            onSuccess={handleSuccess}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xl"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? "100%" : "92vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: isMobile ? 0 : 2,
          overflow: "hidden",
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #0A395F 0%, #1565C0 100%)",
          color: "white",
          py: 2,
          px: 3,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AutoAwesomeIcon sx={{ color: "rgba(255,255,255,0.85)", fontSize: 22 }} />
            <Box>
              <Typography
                sx={{
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  lineHeight: 1.2,
                }}
              >
                Alta Masiva con OCR
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.7)", display: "block", mt: 0.25 }}
              >
                Caja #{idCaja} &mdash; Carga y digitaliza contenidos de forma automatica
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ color: "#FFFFFF" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* ── Stepper ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: { xs: 1, sm: 3 },
          pt: 2,
          pb: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "#f8f9fc",
          flexShrink: 0,
        }}
      >
        <Stepper activeStep={state.activeStep} alternativeLabel>
          {ALL_STEPS.map((step, index) => {
            const isRegioneStep = step.key === "regiones";
            const isOcrStep = step.key === "ocr";
            const skipped =
              (isRegioneStep && (hasPlantilla || !state.useOcr)) ||
              (isOcrStep && (!state.useOcr || (!hasPlantilla && !(state.regiones?.length > 0))));
            return (
              <Step key={step.key} completed={state.activeStep > index}>
                <StyledStepLabel
                  StepIconComponent={() => {
                    const isActive = state.activeStep === index;
                    const isCompleted = state.activeStep > index;
                    let bgColor = "#e0e0e0";
                    let color = "#9e9e9e";
                    if (skipped) { bgColor = "#f5f5f5"; color = "#bdbdbd"; }
                    else if (isCompleted) { bgColor = "#1565C0"; color = "#fff"; }
                    else if (isActive) { bgColor = "#1565C0"; color = "#fff"; }
                    return (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          backgroundColor: bgColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: skipped ? 0.4 : 1,
                          transition: "all 0.2s",
                          boxShadow: isActive ? "0 0 0 3px rgba(21,101,192,0.2)" : "none",
                        }}
                      >
                        <Box sx={{ color, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 16 } }}>
                          {step.icon}
                        </Box>
                      </Box>
                    );
                  }}
                  optional={
                    skipped ? (
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                        Omitido
                      </Typography>
                    ) : null
                  }
                  sx={{ opacity: skipped ? 0.5 : 1 }}
                >
                  {step.label}
                </StyledStepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <DialogContent sx={{ p: 0, flex: 1, overflow: "auto", backgroundColor: "#fafbfd" }}>
        {renderStep()}
      </DialogContent>

      {/* ── Actions ─────────────────────────────────────────────────────────── */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "#fff",
          flexShrink: 0,
          gap: 1,
        }}
      >
        <Button onClick={handleClose} color="inherit" variant="text">
          Cancelar
        </Button>
        <Box sx={{ flex: 1 }} />
        {!isFirstStep && !isLastStep && (
          <Button onClick={handleBack} variant="outlined" color="primary">
            Atras
          </Button>
        )}
        {!isLastStep && (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={!canAdvance}
            sx={{
              background: canAdvance
                ? "linear-gradient(135deg, #1565C0 0%, #1976d2 100%)"
                : undefined,
              px: 3,
            }}
          >
            Siguiente
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
